import { TablerIcon } from '@tabler/icons-react';
import {
  alpha,
  Avatar,
  Skeleton,
  SvgIcon,
  SvgIconProps,
  Theme,
  Typography,
} from '../material';

type FeatureIconColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'aurora'
  | 'blank';

export type FeatureIconProps = {
  content: TablerIcon | string | number;
  size?: Exclude<SvgIconProps['fontSize'], 'xSmall' | 'xxLarge' | 'inherit'>;
  color?: FeatureIconColor;
  dataCy?: string;
};

const sizeConfig = {
  small: { boxSize: 32, fontSize: 'medium', variant: 'caption' },
  medium: { boxSize: 40, fontSize: 'medium', variant: 'caption' },
  large: { boxSize: 48, fontSize: 'large', variant: 'body2' },
  xLarge: { boxSize: 56, fontSize: 'xLarge', variant: 'body1' },
} as const;

// Resolves the main palette color for the given color variant.
// Returns null for 'blank' so the icon renders without a colored background.
const getMainColor = (theme: Theme, color: FeatureIconColor) => {
  if (color === 'blank') {
    return null;
  }

  return theme.palette[color].main;
};

// Formats text/number content to fit inside the avatar.
// Numbers above 99 are capped to "99+"; strings are truncated to 3 characters.
const formatTextContent = (value: string | number) => {
  if (typeof value === 'number') {
    return value > 99 ? '99+' : String(value);
  }
  return value.slice(0, 3);
};

export const FeatureIcon = ({
  content,
  size = 'medium',
  color = 'blank',
  dataCy,
}: FeatureIconProps) => {
  const { boxSize, fontSize, variant } = sizeConfig[size];
  const isTextContent =
    typeof content === 'string' || typeof content === 'number';
  const Icon = isTextContent ? null : content;

  return (
    <Avatar
      sx={[
        t => {
          const mainColor = getMainColor(t, color);
          const sizeStyles = {
            width: boxSize,
            height: boxSize,
          };
          const backgroundStyles = {
            backgroundColor: mainColor ? alpha(mainColor, 0.1) : 'transparent',
          };
          const borderStyles = {
            border: mainColor ? 'none' : `1px solid ${t.palette.divider}`,
          };

          return {
            ...sizeStyles,
            ...backgroundStyles,
            ...borderStyles,
          };
        },
      ]}
      data-cy={dataCy}
    >
      {isTextContent ? (
        <Typography
          component="span"
          variant={variant}
          sx={{
            fontWeight: 500,
            color: `${color}.main`,
            lineHeight: 1,
          }}
        >
          {formatTextContent(content)}
        </Typography>
      ) : Icon ? (
        <SvgIcon fontSize={fontSize} sx={{ color: `${color}.main` }}>
          <Icon />
        </SvgIcon>
      ) : null}
    </Avatar>
  );
};

export const FeatureIconSkeleton = ({
  size = 'medium',
}: {
  size: FeatureIconProps['size'];
}) => {
  const { boxSize } = sizeConfig[size];
  return <Skeleton variant="circular" width={boxSize} height={boxSize} />;
};
